<script setup lang="ts">
import { useDialogStore } from '@/stores/dialogStore';
import { computed, onMounted } from 'vue';

const dialogStore = useDialogStore();
const dialogOption = computed(() => dialogStore.dialogOption);

const handleAlertClick = () => {
  //콜백함수가 있으면 먼저 실행
  dialogOption.value.callback && dialogOption.value.callback();
  dialogStore.closeAlert();
};

const handleConfirmClick = (isConfirm: boolean) => {
  //콜백함수가 있으면 먼저 실행
  dialogOption.value.callback && isConfirm && dialogOption.value.callback();
  dialogStore.closeConfirm();
};
</script>

<template>
  <v-dialog
    v-model="dialogOption.open"
    persistent
    width="auto"
  >
    <div class="dialog-container">
      <div class="dialog-card">
        <!-- Header / Title -->
        <div v-if="dialogOption.title" class="dialog-header">
          <h3 class="dialog-title">{{ dialogOption.title }}</h3>
        </div>

        <!-- Body / Message -->
        <div class="dialog-body">
          <p class="dialog-message">{{ dialogOption.message }}</p>
        </div>

        <!-- Footer / Actions -->
        <div class="dialog-footer">
          <template v-if="dialogOption.type === 'alert'">
            <button
              class="btn-primary"
              @click="handleAlertClick"
            >
              {{ dialogOption.buttonText || '확인' }}
            </button>
          </template>
          
          <template v-if="dialogOption.type === 'confirm'">
            <button
              class="btn-secondary"
              @click="handleConfirmClick(false)"
            >
              취소
            </button>
            <button
              class="btn-primary"
              @click="handleConfirmClick(true)"
            >
              {{ dialogOption.buttonText || '확인' }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.dialog-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.dialog-card {
  background-color: #ffffff;
  border-radius: 1.25rem; // 20px
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 2px 10px -2px rgba(0, 0, 0, 0.04); // boxShadow: premium
  width: 90vw;
  min-width: 320px;
  max-width: 440px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 2.5rem 2rem;
  text-align: center;
}

.dialog-header {
  margin-bottom: 1rem;
}

.dialog-title {
  font-family: 'NanumSquare', sans-serif;
  font-weight: 800;
  font-size: 1.25rem;
  color: #404040; // neutral.DEFAULT
  margin: 0;
}

.dialog-body {
  margin-bottom: 2rem;
}

.dialog-message {
  font-family: 'NanumSquare', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  line-height: 1.6;
  color: #474747;
  margin: 0;
  white-space: pre-wrap;
  word-break: keep-all;
}

.dialog-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  width: 100%;
}

// Button Styles based on Design System
.btn-primary {
  background-color: #EE8544; // primary.500
  color: #ffffff;
  font-family: 'NanumSquare', sans-serif;
  font-weight: 800;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
  border-radius: 0.75rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  flex: 1;

  &:hover {
    background-color: #ea580c; // primary.600
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.btn-secondary {
  background-color: #f3f4f6; // neutral.100
  color: #404040; // neutral.700
  font-family: 'NanumSquare', sans-serif;
  font-weight: 800;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
  border-radius: 0.75rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  flex: 1;

  &:hover {
    background-color: #e5e7eb; // neutral.200
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
